import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import StrategyPanel from '../components/StrategyPanel';
import { notifyError, notify } from '../notify';

// The account's Reddit profile (its Overview tab)
const profileUrl = (account) => `https://www.reddit.com/user/${encodeURIComponent(String(account.reddit_username || '').replace(/^\/?u\//i, ''))}/`;

function Accounts({ creatorAccountIds, selectedCreator, onAccountsChanged, role }) {
  const [accounts, setAccounts] = useState([]);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [linkBioId, setLinkBioId] = useState(null);
  const [strategyId, setStrategyId] = useState(null);
  const [linkBioToken, setLinkBioToken] = useState('');
  const [syncing, setSyncing] = useState(null);
  const [bioLinks, setBioLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  // Runs on: Dolphin browser or a phone of the fleet (admins)
  const [runsOnId, setRunsOnId] = useState(null);
  const [runsOn, setRunsOn] = useState({ executor_type: 'extension', fleet_device_serial: '', fleet_profile_serial: '', fleet_persona_key: '' });
  const [runsOnBusy, setRunsOnBusy] = useState(false);
  const isAdmin = role === 'admin' || role === 'super_admin';

  // Latest filter + request counter: a late response (or a call made from a
  // stale closure) must not overwrite a newer list.
  const idsRef = useRef(creatorAccountIds);
  idsRef.current = creatorAccountIds;
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorAccountIds]);

  const fetchAccounts = async () => {
    const seq = ++fetchSeqRef.current;
    try {
      const res = await axios.get(`${API}/api/accounts`);
      if (seq !== fetchSeqRef.current) return;
      const ids = idsRef.current;
      const filtered = ids
        ? res.data.filter(a => ids.includes(a.id))
        : res.data;
      setAccounts(filtered);
    } catch (err) {
      notifyError(err, 'Could not load accounts');
    }
  };

  const addAccount = async () => {
    if (!name || !username) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/accounts`, {
        name,
        reddit_username: username
      });
      // New accounts always go to the organisation's "Unassigned" space (the
      // server puts them there); they're assigned to a creator later.
      setName('');
      setUsername('');
      // Refresh the creators (and so creatorAccountIds) — otherwise the new
      // account stays filtered out until a page reload.
      if (onAccountsChanged) await onAccountsChanged();
      fetchAccounts();
      const inView = !selectedCreator || selectedCreator.name === 'Unassigned';
      notify(inView ? `Account u/${username} added to Unassigned`
        : `Account u/${username} added to Unassigned — select the Unassigned creator to see it and assign it`, 'success', inView ? 3000 : 7000);
    } catch (err) {
      notifyError(err, 'Could not add account');
    }
    setLoading(false);
  };

  // Background-mode experiment (extension 1.35+): the automation may run with
  // its window behind other windows. Off = the normal way (window in front).
  const togglePostingMode = async (account) => {
    const on = account.posting_mode !== 'background';
    const msg = on
      ? `Turn on background mode for u/${account.reddit_username}?\n\nAn experiment: the posting window may stay behind other windows (never minimized). The profile must be started with these launch flags, or Chrome shows a debugging bar and slows the window:\n--silent-debugger-extension-api --disable-backgrounding-occluded-windows --disable-background-timer-throttling\n\nCompare the account's results in Statistics → Attempts.`
      : `Turn background mode off for u/${account.reddit_username}? Posting goes back to the window in front.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.put(`${API}/api/accounts/${account.id}`, { posting_mode: on ? 'background' : 'foreground' });
      notify(on ? 'Background mode on — it applies from the next run' : 'Background mode off', 'success');
      fetchAccounts();
    } catch (err) { notifyError(err, 'Could not change the posting mode'); }
  };

  const openRunsOn = (account) => {
    if (runsOnId === account.id) { setRunsOnId(null); return; }
    setRunsOnId(account.id);
    setRunsOn({ executor_type: account.executor_type || 'extension', fleet_device_serial: account.fleet_device_serial || '',
      fleet_profile_serial: account.fleet_profile_serial ?? '', fleet_persona_key: account.fleet_persona_key || '' });
  };
  // A phone account never runs in Dolphin: switching to Phone logs its
  // browsers out of the RM tool; back to Dolphin cancels its phone jobs
  const saveRunsOn = async (account) => {
    const phone = runsOn.executor_type === 'fleet';
    const was = account.executor_type === 'fleet';
    if (phone !== was && !window.confirm(phone
      ? `Run u/${account.reddit_username} on a phone?\n\nIts Dolphin browsers are logged out of the RM tool and stop working this account. Launch, stats and replies go to the phone fleet.`
      : `Run u/${account.reddit_username} in a Dolphin browser again?\n\nIts open phone jobs are cancelled and today's replying is switched off.`)) return;
    setRunsOnBusy(true);
    try {
      const body = phone ? { executor_type: 'fleet', fleet_device_serial: runsOn.fleet_device_serial.trim(), fleet_profile_serial: Number(runsOn.fleet_profile_serial), fleet_persona_key: runsOn.fleet_persona_key.trim() || null }
        : { executor_type: 'extension' };
      const { data } = await axios.put(`${API}/api/accounts/${account.id}/executor`, body);
      const extra = data?.browsers_logged_out ? ` · ${data.browsers_logged_out} browser${data.browsers_logged_out > 1 ? 's' : ''} logged out`
        : data?.phone_jobs_cancelled ? ` · ${data.phone_jobs_cancelled} phone job${data.phone_jobs_cancelled > 1 ? 's' : ''} cancelled` : '';
      notify(`u/${account.reddit_username} runs on ${phone ? 'a phone' : 'Dolphin'}${extra}`, 'success');
      setRunsOnId(null);
      fetchAccounts();
    } catch (err) { notifyError(err, 'Could not change where the account runs'); }
    setRunsOnBusy(false);
  };

  const saveEdit = async (id) => {
    if (!editName) return;
    try {
      await axios.put(`${API}/api/accounts/${id}`, { name: editName });
      setEditingId(null);
      setEditName('');
      fetchAccounts();
    } catch (err) {
      notifyError(err, 'Could not rename account');
    }
  };

  const openLinkBio = async (account) => {
    if (linkBioId === account.id) {
      setLinkBioId(null);
      setBioLinks([]);
      return;
    }
    setLinkBioId(account.id);
    setLinkBioToken(account.linkinbio_token || '');
    setBioLinks([]);

    // Fetch available links if token exists
    if (account.linkinbio_token) {
      setLoadingLinks(true);
      try {
        const res = await axios.get(`${API}/api/link-stats/links?account_id=${account.id}`);
        setBioLinks(res.data || []);
      } catch (err) {
        notifyError(err, 'Could not load bio links');
      }
      setLoadingLinks(false);
    }
  };

  const saveLinkBio = async (id) => {
    try {
      await axios.put(`${API}/api/accounts/${id}`, {
        linkinbio_token: linkBioToken || null
      });
      setLinkBioId(null);
      fetchAccounts();
    } catch (err) {
      notifyError(err, 'Could not save link-in-bio token');
    }
  };

  const setTrackedLink = async (account, linkId) => {
    try {
      await axios.post(`${API}/api/link-stats/links/track`, {
        creator_id: account.creator_id,
        link_id: linkId
      });
      // Update local state
      setBioLinks(prev => prev.map(l => ({ ...l, is_tracked: l.link_id === linkId })));
    } catch (err) {
      notifyError(err, 'Could not set tracked link');
    }
  };

  const syncLinkStats = async (id) => {
    setSyncing(id);
    try {
      const res = await axios.post(`${API}/api/link-stats/sync`, { account_id: id });
      const result = res.data?.results?.[0];
      if (result?.error) {
        alert('Sync error: ' + result.error);
      } else {
        alert(`Synced: ${result?.clicks || 0} clicks, ${result?.views || 0} views, ${result?.links || 0} links`);
        // Refresh links after sync
        try {
          const linkRes = await axios.get(`${API}/api/link-stats/links?account_id=${id}`);
          setBioLinks(linkRes.data || []);
        } catch (e) {}
      }
    } catch (err) {
      alert('Sync failed: ' + (err.response?.data?.error || err.message));
    }
    setSyncing(null);
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account? This will also delete all associated posting lists, sessions and posts.')) return;
    try {
      await axios.delete(`${API}/api/accounts/${id}`);
      if (onAccountsChanged) onAccountsChanged();
      fetchAccounts();
    } catch (err) {
      notifyError(err, 'Cannot delete account');
    }
  };

  return (
    <div className="page">
      <h1>Accounts</h1>
      <p>Manage your Reddit client accounts here.</p>

      <div className="card">
        <h2>Add Account</h2>
        <div className="form-row">
          <input
            type="text"
            placeholder="Client name (e.g. Client A)"
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <input
            type="text"
            placeholder="Reddit username"
            value={username}
            onChange={e => setUsername(e.target.value)}
          />
          <button onClick={addAccount} disabled={loading}>
            {loading ? 'Adding...' : 'Add Account'}
          </button>
        </div>
      </div>

      <div className="card">
        <h2>Your Accounts</h2>
        {accounts.length === 0 ? (
          <p className="empty">No accounts yet. Add one above.</p>
        ) : (
          <div className="list">
            {accounts.map(account => (
              <div className="list-item" key={account.id} style={{ flexWrap: 'wrap' }}>
                {editingId === account.id ? (
                  <div className="form-row" style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      placeholder="New name"
                    />
                    <button onClick={() => saveEdit(account.id)}>Save</button>
                    <button className="btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                ) : (
                  <div>
                    <a href={profileUrl(account)} target="_blank" rel="noreferrer" title="Open the Reddit profile in a new tab"
                      style={{ color: 'inherit', textDecoration: 'none' }}>
                      <strong>{account.name}</strong>
                      <span className="sub">u/{account.reddit_username}</span>
                    </a>
                    {account.executor_type === 'fleet' && (
                      <span title={`Runs on a phone: ${account.fleet_device_serial} · profile ${account.fleet_profile_serial}`}
                        style={{ marginLeft: '8px', fontSize: '10px', color: '#0ea5e9', background: '#082f49', padding: '2px 6px', borderRadius: '6px' }}>📱 Phone</span>
                    )}
                    {/* the binding checked against the fleet's profile list (helpers/fleetProfiles.js) */}
                    {account.fleet_check?.problem && (
                      <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px' }}>
                        ⚠ Phone problem: {account.fleet_check.problem}{account.fleet_check.hint ? ` — ${account.fleet_check.hint}` : ''}. Nothing goes to this phone until it's fixed.
                      </div>
                    )}
                    {account.fleet_check?.warning && <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>{account.fleet_check.warning}</div>}
                  </div>
                )}
                {editingId !== account.id && (
                  <div className="list-item-actions">
                    <button className="btn-secondary" onClick={() => window.open(profileUrl(account), '_blank', 'noopener')}
                      title="The account's Reddit profile (Overview) in a new tab">👤 Overview</button>
                    <button className="btn-secondary" onClick={() => openLinkBio(account)}
                      style={account.linkinbio_token ? { borderColor: '#22c55e', color: '#22c55e' } : {}}>
                      {account.linkinbio_token ? '🔗 Bio' : 'Link Bio'}
                    </button>
                    {isAdmin && (
                      <button className="btn-secondary" onClick={() => openRunsOn(account)}
                        title="Where this account runs: a Dolphin browser with the extension, or a phone of the fleet — never both"
                        style={runsOnId === account.id ? { borderColor: '#0ea5e9', color: '#0ea5e9' } : {}}>{account.executor_type === 'fleet' ? '📱 Runs on: Phone' : '🖥 Runs on: Dolphin'}</button>
                    )}
                    {account.executor_type !== 'fleet' && <button className="btn-secondary" onClick={() => togglePostingMode(account)}
                      title="Experiment: the automation window may stay behind other windows. Needs the profile's launch flags --silent-debugger-extension-api --disable-backgrounding-occluded-windows --disable-background-timer-throttling (Dolphin: set via its API); never minimize the window."
                      style={account.posting_mode === 'background' ? { borderColor: '#a855f7', color: '#a855f7' } : {}}>
                      {account.posting_mode === 'background' ? '🧪 Background mode' : 'Background mode'}
                    </button>}
                    <button className="btn-secondary" onClick={() => setStrategyId(strategyId === account.id ? null : account.id)}
                      style={strategyId === account.id ? { borderColor: '#3b82f6', color: '#3b82f6' } : {}}>🎯 Strategy</button>
                    <button className="btn-secondary" onClick={() => {
                      setEditingId(account.id);
                      setEditName(account.name);
                    }}>Rename</button>
                    {role === 'admin' && (
                      <button className="btn-danger" onClick={() => deleteAccount(account.id)}>Delete</button>
                    )}
                  </div>
                )}

                {runsOnId === account.id && (
                  <div style={{ width: '100%', marginTop: '12px', padding: '12px', background: '#0f0f0f', borderRadius: '8px', border: '1px solid #2a2a2a', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px' }}>
                      <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input type="radio" id={`runs-on-dolphin-${account.id}`} checked={runsOn.executor_type !== 'fleet'} onChange={() => setRunsOn(r => ({ ...r, executor_type: 'extension' }))} />
                        🖥 Dolphin browser (extension)
                      </label>
                      <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input type="radio" id={`runs-on-phone-${account.id}`} checked={runsOn.executor_type === 'fleet'} onChange={() => setRunsOn(r => ({ ...r, executor_type: 'fleet' }))} />
                        📱 Phone (fleet)
                      </label>
                    </div>
                    {runsOn.executor_type === 'fleet' && (
                      <div className="form-row" style={{ flexWrap: 'wrap' }}>
                        <input id={`fleet-device-${account.id}`} placeholder="Device serial (e.g. 64011JEBF16408)" value={runsOn.fleet_device_serial}
                          onChange={e => setRunsOn(r => ({ ...r, fleet_device_serial: e.target.value }))} style={{ flex: 2, minWidth: '180px', fontFamily: 'monospace' }} />
                        <input id={`fleet-profile-${account.id}`} placeholder="Android user serial" inputMode="numeric" value={runsOn.fleet_profile_serial}
                          onChange={e => setRunsOn(r => ({ ...r, fleet_profile_serial: e.target.value.replace(/\D/g, '') }))} style={{ flex: 1, minWidth: '120px', fontFamily: 'monospace' }} />
                        <input id={`fleet-persona-${account.id}`} placeholder="Persona key (optional)" value={runsOn.fleet_persona_key}
                          onChange={e => setRunsOn(r => ({ ...r, fleet_persona_key: e.target.value }))} style={{ flex: 1, minWidth: '140px' }} />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button disabled={runsOnBusy || (runsOn.executor_type === 'fleet' && (!runsOn.fleet_device_serial.trim() || runsOn.fleet_profile_serial === ''))} onClick={() => saveRunsOn(account)}>{runsOnBusy ? 'Saving…' : 'Save'}</button>
                      <button className="btn-secondary" onClick={() => setRunsOnId(null)}>Cancel</button>
                      <span style={{ fontSize: '11px', color: '#777' }}>The profile serial is the Android user serial number (never reused), not the user id.</span>
                    </div>
                  </div>
                )}
                {strategyId === account.id && (
                  <div style={{ flexBasis: '100%', width: '100%' }}>
                    <StrategyPanel account={account} onClose={() => setStrategyId(null)} />
                  </div>
                )}
                {linkBioId === account.id && (
                  <div style={{
                    width: '100%', marginTop: '12px', padding: '12px',
                    background: '#0f0f0f', borderRadius: '8px', border: '1px solid #2a2a2a'
                  }}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
                      Link-in-Bio Token (from Maverny page settings)
                    </div>
                    <div className="form-row">
                      <input
                        type="password"
                        placeholder="mvny_live_..."
                        value={linkBioToken}
                        onChange={e => setLinkBioToken(e.target.value)}
                        style={{ flex: 1, fontFamily: 'monospace', fontSize: '13px' }}
                      />
                      <button onClick={() => saveLinkBio(account.id)}>Save</button>
                      {account.linkinbio_token && (
                        <button
                          className="btn-secondary"
                          onClick={() => syncLinkStats(account.id)}
                          disabled={syncing === account.id}
                        >
                          {syncing === account.id ? 'Syncing...' : 'Sync Now'}
                        </button>
                      )}
                    </div>
                    {account.linkinbio_last_synced && (
                      <div style={{ fontSize: '11px', color: '#555', marginTop: '6px' }}>
                        Last synced: {new Date(account.linkinbio_last_synced).toLocaleString()}
                      </div>
                    )}

                    {/* Tracked Link Selector */}
                    {account.linkinbio_token && (
                      <div style={{ marginTop: '12px', borderTop: '1px solid #2a2a2a', paddingTop: '12px' }}>
                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
                          OF Link Tracking — select which link to track as conversion
                        </div>
                        {loadingLinks ? (
                          <div style={{ fontSize: '12px', color: '#555' }}>Loading links...</div>
                        ) : bioLinks.length === 0 ? (
                          <div style={{ fontSize: '12px', color: '#555' }}>
                            No links found. Hit "Sync Now" first to pull link data from Maverny.
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {bioLinks.map(link => {
                              const isTracked = link.is_tracked;
                              return (
                                <div
                                  key={link.link_id}
                                  onClick={() => setTrackedLink(account, link.link_id)}
                                  style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '8px 12px', borderRadius: '6px', cursor: 'pointer',
                                    background: isTracked ? '#1a2a0a' : '#111',
                                    border: isTracked ? '1px solid #22c55e' : '1px solid #2a2a2a',
                                    transition: 'all 0.15s'
                                  }}
                                >
                                  <div>
                                    <span style={{ fontSize: '13px', color: isTracked ? '#22c55e' : '#ccc', fontWeight: isTracked ? '600' : '400' }}>
                                      {isTracked ? '✓ ' : ''}{link.title}
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#555', marginLeft: '8px' }}>
                                      {link.url ? new URL(link.url).hostname.replace('www.', '') : ''}
                                    </span>
                                  </div>
                                  {isTracked && (
                                    <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: '600', padding: '2px 8px', background: '#0a2a0a', borderRadius: '10px' }}>
                                      TRACKING
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Accounts;